import { listLegalTermsQuery } from "@/api/legalTerms";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableFooter } from "@/components/data-table/DataTableFooter";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Label, Select } from "@givenwell/components";
import { PlusIcon } from "@givenwell/core-solid-icons";
import { SupplierTermsTarget, TermsDocumentResponse } from "@givenwell/management-api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { fallback, number, object, optional, picklist, pipe, string, toMinValue } from "valibot";

export const Route = createFileRoute("/admin/legal/")({
  component: LegalTermsListPage,
  validateSearch: object({
    page: fallback(pipe(optional(number(), 1), toMinValue(1)), 1),
    "page-size": fallback(pipe(optional(number(), 25), toMinValue(10)), 25),
    audience: fallback(optional(picklist(["supplier", "supporter"])), undefined),
    intent: fallback(optional(picklist(["marketplace", "eap"])), undefined),
    country: fallback(optional(string()), undefined),
  }),
});

function LegalTermsListPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { page, "page-size": pageSize, audience, intent, country } = search;

  const list = useQuery(
    listLegalTermsQuery({
      page,
      pageSize,
      audience: audience ?? undefined,
      appliesWhenIntent: audience === "supplier" ? (intent ?? undefined) : undefined,
      countryCode: country,
    }),
  );

  return (
    <PageWrapper
      title="Legal Terms"
      rightContent={
        <Link to="/admin/legal/new">
          <Button variant="tonal" color="primary" leftIcon={<PlusIcon />}>
            New document
          </Button>
        </Link>
      }
    >
      <DataTable>
        <DataTableHeader hideToggle>
          <Label>Audience</Label>
          <Select
            size="sm"
            css={{ width: "150px" }}
            value={audience ?? ""}
            onValueChange={value =>
              navigate({
                to: ".",
                search: {
                  ...search,
                  audience: (value || undefined) as "supplier" | "supporter" | undefined,
                  intent: value === "supplier" ? search.intent : undefined,
                  page: 1,
                },
              })
            }
          >
            <option value="">All</option>
            <option value="supplier">Supplier</option>
            <option value="supporter">Supporter</option>
          </Select>

          {audience === "supplier" && (
            <>
              <Label>Intent</Label>
              <Select
                size="sm"
                css={{ width: "150px" }}
                value={intent ?? ""}
                onValueChange={value =>
                  navigate({
                    to: ".",
                    search: {
                      ...search,
                      intent: (value || undefined) as "marketplace" | "eap" | undefined,
                      page: 1,
                    },
                  })
                }
              >
                <option value="">All</option>
                <option value="marketplace">Marketplace</option>
                <option value="eap">EAP</option>
              </Select>
            </>
          )}

          <Label>Country</Label>
          <Select
            size="sm"
            css={{ width: "120px" }}
            value={country ?? ""}
            onValueChange={value =>
              navigate({
                to: ".",
                search: { ...search, country: value || undefined, page: 1 },
              })
            }
          >
            <option value="">All</option>
            <option value="NZ">NZ</option>
            <option value="AU">AU</option>
            <option value="SE">SE</option>
            <option value="US">US</option>
            <option value="CA">CA</option>
            <option value="GB">GB</option>
            <option value="IE">IE</option>
          </Select>
        </DataTableHeader>

        <DataTableBody
          data={list.data?.items ?? []}
          dataKey="terms_id"
          status={list.status}
          columns={[
            {
              header: "Name",
              cell: row => (
                <StyledLink to="/admin/legal/$termsId" params={{ termsId: row.terms_id }}>
                  {row.name}
                </StyledLink>
              ),
            },
            {
              header: "Audience",
              width: "120px",
              cell: row => {
                const isSupplier = isSupplierTarget(row.target);
                return <Badge color={isSupplier ? "blue" : "green"}>{isSupplier ? "supplier" : "supporter"}</Badge>;
              },
            },
            {
              header: "Intent",
              width: "110px",
              cell: row => (isSupplierTarget(row.target) ? (row.target.applies_when_intent ?? "—") : "—"),
            },
            {
              header: "Country",
              width: "80px",
              cell: row => row.country_code ?? "—",
            },
            {
              header: "Version",
              width: "80px",
              cell: row => `v${row.version}`,
            },
            {
              header: "State",
              width: "120px",
              cell: row => <StateBadge state={row.state} />,
            },
            {
              header: "Created",
              width: "180px",
              cell: row => new Date(row.created_at).toLocaleString(),
            },
            {
              header: "Created by",
              width: "160px",
              cell: row => row.created_by_name ?? row.created_by ?? "—",
            },
            {
              header: "Activated",
              width: "180px",
              cell: row => (row.activated_at ? new Date(row.activated_at).toLocaleString() : "—"),
            },
            {
              header: "Activated by",
              width: "160px",
              cell: row => row.activated_by_name ?? row.activated_by ?? "—",
            },
            {
              header: "Superseded",
              width: "180px",
              cell: row => (row.superseded_at ? new Date(row.superseded_at).toLocaleString() : "—"),
            },
            {
              header: "Superseded by",
              width: "160px",
              cell: row => row.superseded_by_name ?? row.superseded_by ?? "—",
            },
          ]}
        />
        <DataTableFooter itemCount={list.data?.total_items ?? 0} />
      </DataTable>
    </PageWrapper>
  );
}

function isSupplierTarget(target: TermsDocumentResponse["target"]): target is SupplierTermsTarget {
  return target.type === "supplier" || "applies_when_intent" in target || "supplier_id" in target;
}

function StateBadge({ state }: { state: TermsDocumentResponse["state"] }) {
  switch (state) {
    case "draft":
      return <Badge color="gray">Draft</Badge>;
    case "active":
      return <Badge color="green">Active</Badge>;
    case "superseded":
      return <Badge color="primary">Superseded</Badge>;
  }
}
